import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Typography } from "@mui/material";
import { useContainer, useTemplate } from "containers/templateDetail/store";
import head from "lodash/head";
import * as React from "react";
import { useTemplateShippingProfiles } from "services/template";
import useCountries from "stores/useStore/useCountries";

export default function PriceDisplay({
  quantity,
  discount,
}): React.ReactElement {
  const { id: templateID, cost, grams } = useTemplate();
  const [activeCountryCode] = useContainer.activeCountryCode();
  const [activeShippingProfileID] = useContainer.activeShippingProfileID();
  const { data: shippingProfiles } = useTemplateShippingProfiles({
    id: templateID,
  });

  const activeShippingProfile = React.useMemo(() => {
    return shippingProfiles?.find(
      (item) => item.id === activeShippingProfileID
    );
  }, [activeShippingProfileID]);

  const { data: countries } = useCountries();

  const activeCountry = React.useMemo(() => {
    return countries?.find((item) => item.isoNumeric === activeCountryCode);
  }, [countries, activeCountryCode]);

  const displayCost = React.useMemo(() => {
    return (parseFloat(cost) * discount).toFixed(2);
  }, [cost, discount]);

  const shippingPrice = React.useMemo(() => {
    if (!activeCountry || !activeShippingProfile) {
      return "-";
    }

    if (activeShippingProfile?.type === "DROP_SHIPPING") {
      const shippingRatePrice = head(
        activeShippingProfile?.rates || []
      )?.prices?.find((item) => item.zone_id === activeCountry.zone_id);
      return `$${(
        (parseFloat(shippingRatePrice?.first_price || "0") +
          parseFloat(shippingRatePrice?.additional_price || "0") *
            (quantity - 1)) /
        quantity
      ).toFixed(2)} Shipping Cost`;
    }

    if (activeShippingProfile?.type === "BULK_SHIPPING") {
      const gramPrice = activeShippingProfile.gram_prices.find(
        (item) => item.country_code === activeCountryCode
      );
      if (!gramPrice) {
        return "Country not supported";
      }
      const price = (parseFloat(gramPrice.price_per_gram) * grams).toFixed(2);
      return `$${price} Shipping Cost`;
    }
  }, [grams, quantity, activeShippingProfile, activeCountry]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        py: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          px: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="p"
          sx={{ display: "flex", alignItems: "flex-end", mr: 2, my: 0 }}
        >
          <Typography component="span" variant="h3" display="inline-block">
            {`$${displayCost}`}
          </Typography>
          <Typography
            component="span"
            variant="body2"
            display="inline-block"
            sx={{ ml: 1, position: "relative", bottom: 4 }}
          >
            /&nbsp;&nbsp;Pieces
          </Typography>
        </Box>
        <Box sx={{ display: "flex", mt: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <AddIcon sx={{ position: "relative", bottom: 6, fontSize: 16 }} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-end" }}>
            <Typography
              variant="body2"
              // color="primary"
              sx={{
                position: "relative",
                bottom: 3,
                ml: 2,
                display: "flex",
                alignItems: "center",
              }}
              // color="textSecondary"
            >
              {shippingPrice}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
